import { useEffect, useMemo, useRef, useState } from "react";
import api, { fmtDT, fmtDate, formatApiError } from "@/lib/api";
import { PageHeader, ProgressBar, StatusBadge, Loading, EmptyState } from "@/components/Shared";
import { toast } from "sonner";
import { ShoppingCart, Plus, X, Send, Lock, Search, Check, Upload, List, FileSpreadsheet, Loader2, Trash2, TrendingUp } from "lucide-react";

export default function AdminOperations() {
  const [ops, setOps] = useState(null);
  const [suppliers, setSuppliers] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({});
  const [catalog, setCatalog] = useState([]);
  const [loadingCat, setLoadingCat] = useState(false);
  const [selected, setSelected] = useState({}); // productId -> {price, qty, ...product}
  const [pq, setPq] = useState("");
  const [pcat, setPcat] = useState("");
  const [mode, setMode] = useState("catalog"); // catalog | import
  const [importing, setImporting] = useState(false);
  const importRef = useRef(null);
  const impCounter = useRef(0);

  const load = () => api.get("/admin/operations").then((r) => setOps(r.data)).catch(() => setOps([]));
  useEffect(() => { load(); api.get("/admin/suppliers").then((r) => setSuppliers(r.data)); }, []);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const onSupplier = (e) => {
    const supplier_id = e.target.value;
    const sup = suppliers.find((s) => s.id === supplier_id);
    setForm({ ...form, supplier_id, supplier_name: sup?.name });
    setSelected({});
    if (!supplier_id) { setCatalog([]); return; }
    setLoadingCat(true);
    api.get("/admin/products", { params: { supplier_id } })
      .then((r) => setCatalog(r.data))
      .catch(() => setCatalog([]))
      .finally(() => setLoadingCat(false));
  };

  const toggle = (p) => {
    setSelected((s) => {
      const n = { ...s };
      if (n[p.id]) { delete n[p.id]; }
      else {
        const price = p.purchase_price || (p.public_price ? Math.round(p.public_price * 0.62 * 1000) / 1000 : 0);
        n[p.id] = { name: p.name, brand: p.brand, category: p.category, laboratory: p.supplier_name, price, public_price: p.public_price, qty: p.available_qty || 250 };
      }
      return n;
    });
  };
  const upd = (id, k, v) => setSelected((s) => ({ ...s, [id]: { ...s[id], [k]: v } }));
  const removeSel = (id) => setSelected((s) => { const n = { ...s }; delete n[id]; return n; });

  const handleImport = async (f) => {
    if (!f) return;
    setImporting(true);
    try {
      const fd = new FormData();
      fd.append("file", f);
      const { data } = await api.post("/admin/parse-products", fd);
      setSelected((s) => {
        const n = { ...s };
        data.products.forEach((p) => {
          const key = `imp-${impCounter.current++}`;
          n[key] = { name: p.name, brand: p.brand, category: p.category, laboratory: form.supplier_name || "", price: p.unit_price, public_price: p.public_price, qty: p.available_qty };
        });
        return n;
      });
      toast.success(`${data.count} produits importés (${data.skipped} ignorés)`);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setImporting(false); }
  };

  const categories = useMemo(() => [...new Set(catalog.map((p) => p.category).filter(Boolean))], [catalog]);
  const filtered = catalog.filter((p) => (!pcat || p.category === pcat) && (!pq || p.name.toLowerCase().includes(pq.toLowerCase())));
  const selCount = Object.keys(selected).length;
  const selList = Object.entries(selected);
  const estTotal = Object.values(selected).reduce((s, x) => s + (parseFloat(x.price) || 0) * 25, 0);

  const openModal = () => { setForm({}); setSelected({}); setCatalog([]); setPq(""); setPcat(""); setMode("catalog"); setOpen(true); };

  const save = async () => {
    if (!form.name || !form.supplier_id || !form.close_date) { toast.error("Nom, fournisseur et date de clôture requis"); return; }
    if (selCount === 0) { toast.error("Sélectionnez ou importez au moins un produit"); return; }
    try {
      const products = Object.entries(selected).map(([pid, x]) => ({
        product_id: pid.startsWith("imp-") ? undefined : pid,
        name: x.name, laboratory: x.laboratory, brand: x.brand, category: x.category,
        unit_price: parseFloat(x.price) || 0,
        public_price: x.public_price ? parseFloat(x.public_price) : undefined,
        available_qty: parseInt(x.qty, 10) || 0,
      }));
      const payload = {
        name: form.name, supplier_id: form.supplier_id, description: form.description,
        category: form.category || "Parapharmacie", laboratory: form.laboratory,
        objective_amount: parseFloat(form.objective_amount) || Math.round(estTotal),
        unit_per_pharmacy: parseFloat(form.unit_per_pharmacy || 0),
        min_per_pharmacy: parseFloat(form.min_per_pharmacy || 0),
        max_per_pharmacy: parseFloat(form.max_per_pharmacy || 0),
        open_date: new Date(form.open_date || Date.now()).toISOString(),
        close_date: new Date(form.close_date).toISOString(),
        conditions: form.conditions, delivery_conditions: form.delivery_conditions, billing_conditions: form.billing_conditions,
        products,
      };
      await api.post("/admin/operations", payload);
      toast.success("Opération créée et publiée");
      setOpen(false); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const action = async (id, verb) => {
    try {
      await api.post(`/admin/operations/${id}/${verb}`);
      toast.success(verb === "close" ? "Opération clôturée" : "Commandes transmises au fournisseur");
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  if (ops === null) return <Loading />;
  const input = "mt-1 w-full h-10 px-3 rounded-lg border border-slate-300 focus:outline-none focus:ring-2 focus:ring-indigo-600 transition-shadow text-sm";

  return (
    <div>
      <PageHeader title="Achats groupés" subtitle="Créez et pilotez les opérations d'achat groupé.">
        <button onClick={openModal} data-testid="add-operation" className="inline-flex items-center gap-2 px-4 py-2.5 rounded-lg bg-indigo-600 hover:bg-indigo-700 text-white text-sm font-semibold transition-colors duration-200"><Plus className="h-4 w-4" /> Nouvelle opération</button>
      </PageHeader>

      {ops.length === 0 ? <EmptyState title="Aucune opération" icon={ShoppingCart} /> : (
        <div className="space-y-4">
          {ops.map((op) => (
            <div key={op.id} className="bg-white border border-slate-200 rounded-xl p-5" data-testid={`admin-op-${op.id}`}>
              <div className="flex flex-col md:flex-row md:items-center gap-4">
                <div className="flex-1">
                  <div className="flex items-center gap-2"><span className="font-bold text-slate-900">{op.name}</span><StatusBadge status={op.status} /></div>
                  <div className="text-xs text-slate-400 mt-0.5">{op.supplier_name} · {op.products?.length || 0} produits · Clôture {fmtDate(op.close_date)} · {op.participants_count} participants</div>
                  <div className="mt-3 max-w-md"><ProgressBar current={op.committed_amount} target={op.objective_amount} /></div>
                  {op.margin_lines > 0 && (
                    <div className="mt-3 flex items-center gap-5 flex-wrap text-xs" data-testid={`margin-${op.id}`}>
                      <span className="inline-flex items-center gap-1.5 text-emerald-700 font-semibold"><TrendingUp className="h-3.5 w-3.5" /> Marge moyenne <span className="num bg-emerald-50 px-1.5 py-0.5 rounded">{op.margin_pct_avg != null ? `+${op.margin_pct_avg}%` : "—"}</span></span>
                      <span className="text-slate-500">Déjà commandé : <span className="num font-bold text-emerald-700">{fmtDT(op.margin_sold)}</span></span>
                      <span className="text-slate-400">Potentiel : <span className="num font-semibold text-slate-700">{fmtDT(op.margin_potential)}</span></span>
                      <span className="text-slate-400">({op.margin_lines}/{op.products?.length || 0} lignes avec PV)</span>
                    </div>
                  )}
                </div>
                <div className="flex gap-2">
                  {op.status === "open" && <button onClick={() => action(op.id, "close")} data-testid={`close-${op.id}`} className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg bg-slate-100 hover:bg-slate-200 text-slate-700 text-sm font-semibold transition-colors duration-200"><Lock className="h-4 w-4" /> Clôturer</button>}
                  {op.status !== "transmitted" && <button onClick={() => action(op.id, "transmit")} data-testid={`transmit-${op.id}`} className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg bg-indigo-50 hover:bg-indigo-100 text-indigo-700 text-sm font-semibold transition-colors duration-200"><Send className="h-4 w-4" /> Transmettre</button>}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {open && (
        <div className="fixed inset-0 z-50 bg-black/40 grid place-items-center p-4" onClick={() => setOpen(false)}>
          <div className="bg-white rounded-2xl w-full max-w-4xl my-8 max-h-[90vh] flex flex-col" onClick={(e) => e.stopPropagation()} data-testid="operation-modal">
            <div className="flex items-center justify-between p-5 border-b border-slate-200">
              <h3 className="font-bold text-lg text-slate-900">Nouvelle opération groupée</h3>
              <button onClick={() => setOpen(false)}><X className="h-5 w-5 text-slate-400" /></button>
            </div>

            <div className="p-5 overflow-y-auto grid lg:grid-cols-2 gap-6">
              {/* Left: operation fields */}
              <div className="space-y-3">
                <div><label className="text-sm font-semibold text-slate-700">Nom de l'opération</label><input data-testid="op-name" onChange={set("name")} className={input} /></div>
                <div><label className="text-sm font-semibold text-slate-700">Fournisseur</label>
                  <select onChange={onSupplier} className={input} data-testid="op-supplier"><option value="">Choisir…</option>{suppliers.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}</select></div>
                <div><label className="text-sm font-semibold text-slate-700">Description</label><textarea onChange={set("description")} className={input + " h-16 py-2"} /></div>
                <div className="grid grid-cols-3 gap-3">
                  <div><label className="text-sm font-semibold text-slate-700">Objectif (DT)</label><input data-testid="op-objective" type="number" placeholder={Math.round(estTotal) || ""} onChange={set("objective_amount")} className={input} /></div>
                  <div><label className="text-sm font-semibold text-slate-700">Min/pharm.</label><input type="number" onChange={set("min_per_pharmacy")} className={input} /></div>
                  <div><label className="text-sm font-semibold text-slate-700">Max/pharm.</label><input type="number" onChange={set("max_per_pharmacy")} className={input} /></div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div><label className="text-sm font-semibold text-slate-700">Ouverture</label><input type="date" onChange={set("open_date")} className={input} /></div>
                  <div><label className="text-sm font-semibold text-slate-700">Clôture</label><input data-testid="op-close" type="date" onChange={set("close_date")} className={input} /></div>
                </div>
                <div><label className="text-sm font-semibold text-slate-700">Conditions</label><input onChange={set("conditions")} className={input} placeholder="Remise, livraison, facturation..." /></div>
              </div>

              {/* Right: product picker */}
              <div className="flex flex-col border border-slate-200 rounded-xl overflow-hidden">
                <div className="px-4 py-3 border-b border-slate-200 bg-slate-50">
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-bold text-slate-800">Produits de l'opération</span>
                    <span className="text-xs font-semibold text-indigo-600 num" data-testid="op-selected-count">{selCount} sélectionné(s)</span>
                  </div>
                  {/* Mode toggle */}
                  <div className="mt-2 grid grid-cols-2 gap-1 p-1 bg-slate-200/60 rounded-lg">
                    <button type="button" onClick={() => setMode("catalog")} data-testid="mode-catalog" className={`h-8 rounded-md text-xs font-semibold flex items-center justify-center gap-1.5 transition-colors duration-200 ${mode === "catalog" ? "bg-white text-indigo-600 shadow-sm" : "text-slate-500"}`}><List className="h-3.5 w-3.5" /> Catalogue</button>
                    <button type="button" onClick={() => setMode("import")} data-testid="mode-import" className={`h-8 rounded-md text-xs font-semibold flex items-center justify-center gap-1.5 transition-colors duration-200 ${mode === "import" ? "bg-white text-indigo-600 shadow-sm" : "text-slate-500"}`}><Upload className="h-3.5 w-3.5" /> Importer fichier</button>
                  </div>
                  {mode === "catalog" && (
                    <div className="mt-2 flex gap-2">
                      <div className="relative flex-1">
                        <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-slate-400" />
                        <input data-testid="op-catalog-search" value={pq} onChange={(e) => setPq(e.target.value)} placeholder="Rechercher..." className="w-full h-9 pl-8 pr-3 rounded-lg border border-slate-300 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-600" />
                      </div>
                      <select value={pcat} onChange={(e) => setPcat(e.target.value)} className="h-9 px-2 rounded-lg border border-slate-300 text-sm bg-white">
                        <option value="">Catégorie</option>
                        {categories.map((c) => <option key={c} value={c}>{c}</option>)}
                      </select>
                    </div>
                  )}
                </div>

                {/* CATALOG MODE */}
                {mode === "catalog" && (
                  <div className="flex-1 overflow-y-auto max-h-[300px] divide-y divide-slate-100">
                    {!form.supplier_id && <div className="p-6 text-center text-sm text-slate-400">Choisissez d'abord un fournisseur.</div>}
                    {loadingCat && <div className="p-6 text-center text-sm text-slate-400">Chargement du catalogue...</div>}
                    {form.supplier_id && !loadingCat && filtered.length === 0 && <div className="p-6 text-center text-sm text-slate-400">Aucun produit.</div>}
                    {filtered.slice(0, 150).map((p) => {
                      const sel = selected[p.id];
                      return (
                        <div key={p.id} className={`px-3 py-2.5 ${sel ? "bg-indigo-50/50" : ""}`} data-testid={`catalog-item-${p.id}`}>
                          <label className="flex items-start gap-2.5 cursor-pointer">
                            <span className={`mt-0.5 h-5 w-5 rounded border grid place-items-center shrink-0 ${sel ? "bg-indigo-600 border-indigo-600" : "border-slate-300"}`}>
                              {sel && <Check className="h-3.5 w-3.5 text-white" />}
                            </span>
                            <input type="checkbox" className="hidden" checked={!!sel} onChange={() => toggle(p)} data-testid={`catalog-check-${p.id}`} />
                            <div className="flex-1 min-w-0">
                              <div className="text-sm font-semibold text-slate-800 truncate">{p.name}</div>
                              <div className="text-xs text-slate-400">{p.category}{p.public_price ? ` · public ${fmtDT(p.public_price)}` : ""}</div>
                            </div>
                          </label>
                          {sel && (
                            <div className="mt-2 ml-7 flex flex-wrap items-center gap-2">
                              <div className="flex items-center gap-1"><span className="text-xs text-slate-500">PA</span>
                                <input type="number" value={sel.price} onChange={(e) => upd(p.id, "price", e.target.value)} data-testid={`sel-price-${p.id}`} className="h-8 w-20 px-2 rounded border border-slate-300 text-sm num" /></div>
                              <div className="flex items-center gap-1"><span className="text-xs text-slate-500">PV</span>
                                <input type="number" value={sel.public_price || ""} onChange={(e) => upd(p.id, "public_price", e.target.value)} data-testid={`sel-pv-${p.id}`} className="h-8 w-20 px-2 rounded border border-slate-300 text-sm num" placeholder="public" /></div>
                              <div className="flex items-center gap-1"><span className="text-xs text-slate-500">Stock</span>
                                <input type="number" value={sel.qty} onChange={(e) => upd(p.id, "qty", e.target.value)} data-testid={`sel-qty-${p.id}`} className="h-8 w-20 px-2 rounded border border-slate-300 text-sm num" /></div>
                              {sel.public_price > 0 && sel.price > 0 && (
                                <span className="text-xs font-bold text-emerald-600 num">+{(((parseFloat(sel.public_price) - parseFloat(sel.price)) / parseFloat(sel.price)) * 100).toFixed(0)}%</span>
                              )}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}

                {/* IMPORT MODE */}
                {mode === "import" && (
                  <div className="flex-1 overflow-y-auto max-h-[300px]">
                    <div className="p-4">
                      <input ref={importRef} type="file" accept=".xlsx,.xls,.csv" className="hidden" data-testid="op-import-input"
                        onChange={(e) => { handleImport(e.target.files[0]); e.target.value = ""; }} />
                      <button type="button" onClick={() => importRef.current?.click()} disabled={importing} data-testid="op-import-btn"
                        className="w-full flex items-center gap-3 px-3 py-4 rounded-lg border-2 border-dashed border-slate-300 hover:border-indigo-400 transition-colors duration-200 disabled:opacity-60">
                        {importing ? <Loader2 className="h-5 w-5 text-indigo-500 animate-spin" /> : <FileSpreadsheet className="h-5 w-5 text-indigo-500" />}
                        <span className="text-sm text-slate-500">{importing ? "Analyse du fichier..." : "Choisir un fichier .xlsx, .xls ou .csv"}</span>
                      </button>
                      <p className="mt-2 text-xs text-slate-400">Les produits importés s'ajoutent à la sélection ci-dessous. Vous pouvez ajuster prix et stock.</p>
                    </div>
                    <div className="divide-y divide-slate-100 border-t border-slate-100">
                      <div className="px-3 py-2 bg-slate-50 text-xs font-bold text-slate-500 uppercase tracking-wide">Sélection ({selCount})</div>
                      {selList.length === 0 && <div className="p-6 text-center text-sm text-slate-400">Importez un fichier ou choisissez des produits dans le catalogue.</div>}
                      {selList.map(([id, x]) => (
                        <div key={id} className="px-3 py-2.5" data-testid={`sel-item-${id}`}>
                          <div className="flex items-start gap-2">
                            <div className="flex-1 min-w-0">
                              <div className="text-sm font-semibold text-slate-800 truncate">{x.name}</div>
                              <div className="text-xs text-slate-400">{x.category}{x.brand ? ` · ${x.brand}` : ""}</div>
                            </div>
                            <button onClick={() => removeSel(id)} className="text-slate-400 hover:text-rose-500 transition-colors duration-200"><Trash2 className="h-4 w-4" /></button>
                          </div>
                          <div className="mt-2 flex flex-wrap items-center gap-2">
                            <div className="flex items-center gap-1"><span className="text-xs text-slate-500">PA</span>
                              <input type="number" value={x.price} onChange={(e) => upd(id, "price", e.target.value)} className="h-8 w-20 px-2 rounded border border-slate-300 text-sm num" /></div>
                            <div className="flex items-center gap-1"><span className="text-xs text-slate-500">PV</span>
                              <input type="number" value={x.public_price || ""} onChange={(e) => upd(id, "public_price", e.target.value)} className="h-8 w-20 px-2 rounded border border-slate-300 text-sm num" placeholder="public" /></div>
                            <div className="flex items-center gap-1"><span className="text-xs text-slate-500">Stock</span>
                              <input type="number" value={x.qty} onChange={(e) => upd(id, "qty", e.target.value)} className="h-8 w-20 px-2 rounded border border-slate-300 text-sm num" /></div>
                            {x.public_price > 0 && x.price > 0 && (
                              <span className="text-xs font-bold text-emerald-600 num">+{(((parseFloat(x.public_price) - parseFloat(x.price)) / parseFloat(x.price)) * 100).toFixed(0)}%</span>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>

            <div className="p-5 border-t border-slate-200 flex items-center justify-between">
              <span className="text-sm text-slate-500">Objectif estimé : <span className="num font-bold text-slate-900">{fmtDT(Math.round(estTotal))}</span></span>
              <button onClick={save} data-testid="save-operation" className="px-6 h-11 rounded-lg bg-indigo-600 hover:bg-indigo-700 text-white font-semibold transition-colors duration-200">Créer et publier</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
